import supabase from '@/supabaseClient'
import { gameService } from '@/services/gameService'
import { GenreService } from './genreService'
import { Game } from '@/types/database'
import { Genre } from '@/types/game'
import {
  SteamGame,
  GameProcessingResult,
  GameOption
} from './types'


export const GameProcessingService = {
  async processSelectedGames(
    steamGames: SteamGame[],
    selectedGames: Set<string>
  ): Promise<GameProcessingResult[]> {
    const selectedGamesData = steamGames.filter((game) =>
      selectedGames.has(game.appId.toString())
    )

    const gameProcessingResults = await Promise.all(
      selectedGamesData.map(async (game) => {
        try {
          const searchResult = await gameService.searchGames(game.name)

          if (searchResult.results.length > 0) {
            const normalizedName = (str: string) =>
              str
                .toLowerCase()
                .replace(/[^\w\s]/gi, '')
                .trim()

            const exactMatch = searchResult.results.find(
              (g) => normalizedName(g.name) === normalizedName(game.name)
            )

            if (!exactMatch) {
              console.warn(
                `⚠️ No exact match for "${game.name}". Using first IGDB result: "${searchResult.results[0].name}"`
              )
            }

            const igdbGame = exactMatch || searchResult.results[0]
            const genres: Genre[] = igdbGame.genres || []

            // Find all possible IGDB matches in our DB
            const { data: possibleMatches } = await supabase
              .from('games')
              .select('*')
              .eq('igdb_id', igdbGame.id.toString())
              .eq('provider', 'igdb')

            let selectedIgdbGame = null
            if (possibleMatches && possibleMatches.length > 1) {
              return {
                type: 'conflict' as const,
                steamGame: game,
                possibleMatches: possibleMatches.map((g: Game): GameOption => ({
                  id: g.id,
                  title: g.title,
                  provider: g.provider,
                  background_image: g.background_image,
                  release_date: g.release_date,
                })),
                genres: genres,
                igdbData: possibleMatches[0]
              }
            } else if (possibleMatches && possibleMatches.length === 1) {
              selectedIgdbGame = possibleMatches[0]
            }

            if (selectedIgdbGame) {
              return {
                steamGame: game,
                igdbData: selectedIgdbGame,
                genres: genres,
                existingIgdbGame: true,
              }
            }

            const newGameData: Partial<Game> = {
              title: game.name,
              igdb_id: igdbGame.id.toString(),
              provider: 'igdb',
              background_image: igdbGame.background_image,
              metacritic_rating: igdbGame.metacritic,
              release_date: igdbGame.released,
              created_at: new Date().toISOString(),
            }

            return {
              steamGame: game,
              igdbData: newGameData as Game,
              genres: genres,
              existingIgdbGame: false,
            }
          } else {
            // No IGDB results found - use Steam data
            const steamGameData: Partial<Game> = {
              title: game.name,
              igdb_id: game.appId.toString(),
              provider: 'steam',
              background_image: game.iconUrl,
              created_at: new Date().toISOString(),
            }

            return {
              steamGame: game,
              igdbData: steamGameData as Game,
              genres: [],
            }
          }
        } catch (error) {
          console.error(`Error fetching IGDB data for ${game.name}:`, error)

          const fallbackGameData: Partial<Game> = {
            title: game.name,
            igdb_id: game.appId.toString(),
            provider: 'steam',
            background_image: game.iconUrl,
            created_at: new Date().toISOString(),
          }

          return {
            steamGame: game,
            igdbData: fallbackGameData as Game,
            genres: [],
          }
        }
      })
    )

    return gameProcessingResults
  },

  async insertGamesAndProcessGenres(resolvedGames: GameProcessingResult[]): Promise<Game[]> {
    const gamesToInsert = resolvedGames
      .filter((data) => !data.existingIgdbGame)
      .map((data) => data.igdbData)

    const existingIgdbGames = resolvedGames
      .filter((data) => data.existingIgdbGame)
      .map((data) => data.igdbData)

    let insertedGames: Game[] = []
    let gamesError = null

    if (gamesToInsert.length > 0) {
      const { data, error } = await supabase
        .from('games')
        .upsert(gamesToInsert, { onConflict: 'igdb_id,provider' })
        .select()

      if (error) {
        gamesError = error
        console.error('Error inserting games:', error)
      } else {
        insertedGames = (data as Game[]) || []
      }
    }

    insertedGames = [...insertedGames, ...(existingIgdbGames as Game[])]

    if (gamesError) {
      throw gamesError
    }

    // Process genres
    for (let i = 0; i < resolvedGames.length; i++) {
      const gameData = resolvedGames[i]
      console.log(
        '[Mapping Debug] Steam game:',
        gameData.steamGame?.name,
        'IGDB data:',
        gameData.igdbData,
        'existingIgdbGame:',
        gameData.existingIgdbGame
      )
      const insertedGame = insertedGames.find((game: Game) => {
        if (gameData.existingIgdbGame) {
          const match = game.id === gameData.igdbData.id
          if (match)
            console.log('[Mapping Debug] Matched by id:', game.id, game.title)
          return match
        }
        const match =
          game.igdb_id === ('igdb_id' in gameData.igdbData ? gameData.igdbData.igdb_id : gameData.igdbData.id.toString()) &&
          game.provider === ('provider' in gameData.igdbData ? gameData.igdbData.provider : 'igdb') &&
          game.title === ('title' in gameData.igdbData ? gameData.igdbData.title : gameData.igdbData.name)
        if (match)
          console.log(
            '[Mapping Debug] Matched by igdb_id/provider/title:',
            game.igdb_id,
            game.provider,
            game.title
          )
        return match
      })

      if (!insertedGame) {
        console.warn(
          '[Mapping Debug] No strict match for',
          gameData.steamGame?.name,
          'Trying lenient match...'
        )
        const lenientMatch = insertedGames.find(
          (game: Game) =>
            game.title === ('title' in gameData.igdbData ? gameData.igdbData.title : gameData.igdbData.name) ||
            game.igdb_id === ('igdb_id' in gameData.igdbData ? gameData.igdbData.igdb_id : gameData.igdbData.id.toString())
        )

        if (lenientMatch) {
          console.log(
            '[Mapping Debug] Lenient match found:',
            lenientMatch.id,
            lenientMatch.title
          )
          await GenreService.processGameGenres(gameData, lenientMatch)
          continue
        } else {
          console.warn(
            '[Mapping Debug] No lenient match for',
            gameData.steamGame?.name
          )
          continue
        }
      }

      console.log(
        '[Mapping Debug] Using insertedGame:',
        insertedGame.id,
        insertedGame.title
      )
      await GenreService.processGameGenres(gameData, insertedGame)
    }

    return insertedGames
  }
}